import gql from 'graphql-tag';
import apolloClient from '../apollo';

const getDefaultState = () => ({
  accessCatalog: [],
  organizationRoles: [],
});

const state = getDefaultState();

const ORGANIZATION_ROLE_FIELDS = gql`
  fragment organizationRoleFields on OrganizationRole {
    _id
    number
    name
    description
    members
    assignments {
      node
      level
    }
    createdAt
    updatedAt
  }
`;

const ACCESS_CATALOG_FIELDS = gql`
  fragment accessCatalogFields on AccessCatalogNode {
    id
    parent
    viewId
    kind
    labelKey
  }
`;

const createRoleInputPayload = (role) => ({
  name: role.name,
  description: role.description || null,
});

const actions = {
  async fetchAccessCatalog({ commit }) {
    const response = await apolloClient.query({
      query: gql`
        query accessCatalog {
          accessCatalog {
            ...accessCatalogFields
          }
        }
        ${ACCESS_CATALOG_FIELDS}
      `,
      fetchPolicy: 'network-only',
    });
    commit('SET_ACCESS_CATALOG', response.data.accessCatalog);
    return response.data.accessCatalog;
  },

  async fetchOrganizationRoles({ commit }) {
    const response = await apolloClient.query({
      query: gql`
        query organizationRoles {
          organizationRoles {
            ...organizationRoleFields
          }
        }
        ${ORGANIZATION_ROLE_FIELDS}
      `,
      fetchPolicy: 'network-only',
    });
    commit('SET_ORGANIZATION_ROLES', response.data.organizationRoles);
    return response.data.organizationRoles;
  },

  async createOrganizationRole({ commit }, role) {
    const response = await apolloClient.mutate({
      mutation: gql`
        mutation createOrganizationRole($input: CreateOrganizationRoleInput!) {
          createOrganizationRole(input: $input) {
            ...organizationRoleFields
          }
        }
        ${ORGANIZATION_ROLE_FIELDS}
      `,
      variables: {
        input: createRoleInputPayload(role),
      },
    });
    commit('ADD_OR_UPDATE_ORGANIZATION_ROLE', response.data.createOrganizationRole);
    return response.data.createOrganizationRole;
  },

  async updateOrganizationRole({ commit }, { number, role }) {
    const response = await apolloClient.mutate({
      mutation: gql`
        mutation updateOrganizationRole($number: Int!, $input: UpdateOrganizationRoleInput!) {
          updateOrganizationRole(number: $number, input: $input) {
            ...organizationRoleFields
          }
        }
        ${ORGANIZATION_ROLE_FIELDS}
      `,
      variables: {
        number: parseInt(number, 10),
        input: createRoleInputPayload(role),
      },
    });
    commit('ADD_OR_UPDATE_ORGANIZATION_ROLE', response.data.updateOrganizationRole);
    return response.data.updateOrganizationRole;
  },

  async deleteOrganizationRole({ commit }, number) {
    await apolloClient.mutate({
      mutation: gql`
        mutation deleteOrganizationRole($number: Int!) {
          deleteOrganizationRole(number: $number)
        }
      `,
      variables: {
        number: parseInt(number, 10),
      },
    });
    commit('REMOVE_ORGANIZATION_ROLE', parseInt(number, 10));
  },

  async setOrganizationRoleMembers({ commit }, { number, userIds }) {
    const response = await apolloClient.mutate({
      mutation: gql`
        mutation setOrganizationRoleMembers($number: Int!, $userIds: [ID!]!) {
          setOrganizationRoleMembers(number: $number, userIds: $userIds) {
            ...organizationRoleFields
          }
        }
        ${ORGANIZATION_ROLE_FIELDS}
      `,
      variables: {
        number: parseInt(number, 10),
        userIds,
      },
    });
    commit('ADD_OR_UPDATE_ORGANIZATION_ROLE', response.data.setOrganizationRoleMembers);
    return response.data.setOrganizationRoleMembers;
  },

  async setOrganizationRoleAssignments({ commit }, { number, assignments }) {
    const response = await apolloClient.mutate({
      mutation: gql`
        mutation setOrganizationRoleAssignments(
          $number: Int!
          $assignments: [OrganizationRoleAssignmentInput!]!
        ) {
          setOrganizationRoleAssignments(number: $number, assignments: $assignments) {
            ...organizationRoleFields
          }
        }
        ${ORGANIZATION_ROLE_FIELDS}
      `,
      variables: {
        number: parseInt(number, 10),
        assignments,
      },
    });
    commit('ADD_OR_UPDATE_ORGANIZATION_ROLE', response.data.setOrganizationRoleAssignments);
    return response.data.setOrganizationRoleAssignments;
  },

  resetOrganizationRoles({ commit }) {
    commit('SET_DEFAULT_STATE');
  },
};

const mutations = {
  SET_DEFAULT_STATE(state) {
    Object.assign(state, getDefaultState());
  },
  SET_ACCESS_CATALOG(state, accessCatalog) {
    state.accessCatalog = accessCatalog || [];
  },
  SET_ORGANIZATION_ROLES(state, organizationRoles) {
    state.organizationRoles = organizationRoles || [];
  },
  ADD_OR_UPDATE_ORGANIZATION_ROLE(state, role) {
    const index = state.organizationRoles.findIndex((entry) => entry._id === role._id);
    if (index === -1) {
      state.organizationRoles.push(role);
      return;
    }
    state.organizationRoles.splice(index, 1, role);
  },
  REMOVE_ORGANIZATION_ROLE(state, roleNumber) {
    state.organizationRoles = state.organizationRoles.filter((role) => role.number !== roleNumber);
  },
};

const getters = {
  getAccessCatalog: (state) => state.accessCatalog,
  getOrganizationRoles: (state) => state.organizationRoles,
  getOrganizationRoleByNumber: (state) => (roleNumber) =>
    state.organizationRoles.find((role) => role.number === parseInt(roleNumber, 10)),
};

export default {
  state,
  getters,
  actions,
  mutations,
};
